import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Camera, RotateCw, RefreshCw, Check, SwitchCamera, Image as ImageIcon, Loader2, Maximize } from 'lucide-react';

// Camera → capture → (optional) crop → hands back a JPEG File via onCapture(file).
// Live preview uses getUserMedia (HTTPS/localhost only). Whenever it is unavailable,
// slow to start, or blocked (common inside the Android app's WebView), the student can
// always fall back to the phone's own camera app or the gallery — both go through the
// same crop step.

const MAX_SIDE = 2000;          // uploaded image size
const WORK_MAX_SIDE = 2400;     // big phone photos are shrunk to this on load (avoids memory crashes)
const MIN_CROP = 0.08;          // smallest crop box, as a fraction of the image
const FULL_CROP = { x: 0, y: 0, w: 1, h: 1 };
const START_TIMEOUT_MS = 6000;  // camera that shows no picture by then counts as failed

const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

// Load any image source, downscale it to WORK_MAX_SIDE, return a JPEG data URL.
function normaliseImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, WORK_MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.naturalWidth * scale);
      canvas.height = Math.round(img.naturalHeight * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.92));
    };
    img.onerror = () => reject(new Error('Could not read this photo'));
    img.src = src;
  });
}

export default function CameraCapture({ open, onClose, onCapture }) {
  const [step, setStep] = useState('camera');          // 'camera' | 'crop'
  const [camState, setCamState] = useState('starting'); // 'starting' | 'ready' | 'failed'
  const [cameraError, setCameraError] = useState(null); // 'unsupported' | 'denied' | 'unavailable' | 'timeout'
  const [facingMode, setFacingMode] = useState('environment');
  const [hasMultipleCams, setHasMultipleCams] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState(FULL_CROP);
  const [processing, setProcessing] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const cropAreaRef = useRef(null);
  const imgRef = useRef(null);
  const dragRef = useRef(null);
  const startTimerRef = useRef(null);
  const closingViaBackRef = useRef(false);
  const startIdRef = useRef(0);          // invalidates camera starts that finish after a stop
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const stopStream = useCallback(() => {
    startIdRef.current += 1;
    clearTimeout(startTimerRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }, []);

  const fail = useCallback((reason) => {
    stopStream();
    setCameraError(reason);
    setCamState('failed');
  }, [stopStream]);

  const startStream = useCallback(async (mode) => {
    stopStream();
    const myId = startIdRef.current;
    setCameraError(null);
    setCamState('starting');
    if (!navigator.mediaDevices?.getUserMedia) {
      fail('unsupported');
      return;
    }
    // Some phones/WebViews never deliver a frame and never error — don't leave the student waiting.
    startTimerRef.current = setTimeout(() => fail('timeout'), START_TIMEOUT_MS);
    try {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: mode }, width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
      } catch (err) {
        if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') throw err;
        // Older Android cameras can reject the resolution hint — retry with no constraints.
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      }
      if (myId !== startIdRef.current) {
        // Closed, captured, or restarted while the camera was starting — release it.
        stream.getTracks().forEach(t => t.stop());
        return;
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }
      navigator.mediaDevices.enumerateDevices?.()
        .then(devices => setHasMultipleCams(devices.filter(d => d.kind === 'videoinput').length > 1))
        .catch(() => {});
    } catch (err) {
      if (myId !== startIdRef.current) return;
      fail(err?.name === 'NotAllowedError' || err?.name === 'SecurityError' ? 'denied' : 'unavailable');
    }
  }, [stopStream, fail]);

  const onVideoReady = () => {
    if (videoRef.current?.videoWidth) {
      clearTimeout(startTimerRef.current);
      setCamState('ready');
    }
  };

  // Open / close lifecycle
  useEffect(() => {
    if (open) {
      setStep('camera');
      setImageSrc(null);
      setCrop(FULL_CROP);
      setLoadError(null);
      startStream(facingMode);
    } else {
      stopStream();
    }
    return stopStream;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Android Back button closes the camera instead of leaving the chat page.
  useEffect(() => {
    if (!open) return;
    window.history.pushState({ cameraOpen: true }, '');
    const onPop = () => {
      closingViaBackRef.current = true;
      stopStream();
      onCloseRef.current();
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      if (!closingViaBackRef.current && window.history.state?.cameraOpen) window.history.back();
      closingViaBackRef.current = false;
    };
  }, [open, stopStream]);

  // Switching apps freezes the stream on many Androids — restart it when the student comes back.
  useEffect(() => {
    if (!open) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible' && step === 'camera' && camState !== 'failed') {
        startStream(facingMode);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [open, step, camState, facingMode, startStream]);

  const handleClose = () => {
    stopStream();
    onClose();
  };

  const switchCamera = () => {
    const next = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(next);
    startStream(next);
  };

  const goToCrop = (dataUrl) => {
    stopStream();
    setImageSrc(dataUrl);
    setCrop(FULL_CROP);
    setStep('crop');
  };

  const capture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const scale = Math.min(1, WORK_MAX_SIDE / Math.max(video.videoWidth, video.videoHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    stopStream();
    goToCrop(canvas.toDataURL('image/jpeg', 0.92));
  };

  // Photo from the phone's camera app or gallery
  const handlePickedFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setLoadError('Please choose a photo (JPG or PNG).');
      return;
    }
    setLoadError(null);
    setProcessing(true);
    stopStream();
    const url = URL.createObjectURL(file);
    normaliseImage(url)
      .then(goToCrop)
      .catch(() => setLoadError('Could not open that photo. Please try another one.'))
      .finally(() => { URL.revokeObjectURL(url); setProcessing(false); });
  };

  // Release the live camera first — many Androids can't give the camera to two apps at once.
  const openPhoneCamera = () => {
    stopStream();
    setCamState('failed');
    setCameraError(prev => prev || 'paused');
    cameraInputRef.current?.click();
  };
  const openGallery = () => galleryInputRef.current?.click();

  const retake = () => {
    setImageSrc(null);
    setStep('camera');
    startStream(facingMode);
  };

  const rotate = () => {
    const img = imgRef.current;
    if (!img) return;
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalHeight;
    canvas.height = img.naturalWidth;
    const ctx = canvas.getContext('2d');
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(Math.PI / 2);
    ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
    setImageSrc(canvas.toDataURL('image/jpeg', 0.92));
    setCrop(FULL_CROP);
  };

  // ---- Crop box dragging (move + corner resize), in fractions of the image ----
  const onPointerDown = (mode) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { mode, startX: e.clientX, startY: e.clientY, start: crop };
  };

  useEffect(() => {
    if (step !== 'crop') return;
    const onMove = (e) => {
      const drag = dragRef.current;
      const area = cropAreaRef.current;
      if (!drag || !area) return;
      const rect = area.getBoundingClientRect();
      const dx = (e.clientX - drag.startX) / rect.width;
      const dy = (e.clientY - drag.startY) / rect.height;
      const s = drag.start;
      let { x, y, w, h } = s;

      if (drag.mode === 'move') {
        x = clamp(s.x + dx, 0, 1 - s.w);
        y = clamp(s.y + dy, 0, 1 - s.h);
      } else {
        if (drag.mode.includes('w')) {
          x = clamp(s.x + dx, 0, s.x + s.w - MIN_CROP);
          w = s.x + s.w - x;
        }
        if (drag.mode.includes('e')) w = clamp(s.w + dx, MIN_CROP, 1 - s.x);
        if (drag.mode.includes('n')) {
          y = clamp(s.y + dy, 0, s.y + s.h - MIN_CROP);
          h = s.y + s.h - y;
        }
        if (drag.mode.includes('s')) h = clamp(s.h + dy, MIN_CROP, 1 - s.y);
      }
      setCrop({ x, y, w, h });
    };
    const onUp = () => { dragRef.current = null; };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [step]);

  const confirmCrop = () => {
    const img = imgRef.current;
    if (!img) return;
    setProcessing(true);
    const sx = Math.round(crop.x * img.naturalWidth);
    const sy = Math.round(crop.y * img.naturalHeight);
    const sw = Math.round(crop.w * img.naturalWidth);
    const sh = Math.round(crop.h * img.naturalHeight);
    const scale = Math.min(1, MAX_SIDE / Math.max(sw, sh));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(sw * scale);
    canvas.height = Math.round(sh * scale);
    canvas.getContext('2d').drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      setProcessing(false);
      if (!blob) return;
      const file = new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' });
      onCapture(file);
      handleClose();
    }, 'image/jpeg', 0.9);
  };

  if (!open) return null;

  const isFullCrop = crop.x === 0 && crop.y === 0 && crop.w === 1 && crop.h === 1;

  // Big invisible touch target (44px) with a visible white corner bracket inside it
  const handle = (pos) => {
    const v = pos[0] === 'n' ? 'top-0 -mt-[22px]' : 'bottom-0 -mb-[22px]';
    const hz = pos[1] === 'w' ? 'left-0 -ml-[22px]' : 'right-0 -mr-[22px]';
    const border = `${pos[0] === 'n' ? 'border-t-4' : 'border-b-4'} ${pos[1] === 'w' ? 'border-l-4' : 'border-r-4'}`;
    return (
      <div
        onPointerDown={onPointerDown(pos)}
        className={`absolute w-11 h-11 flex items-center justify-center ${v} ${hz}`}
        style={{ touchAction: 'none' }}
      >
        <div className={`w-6 h-6 border-white ${border}`} />
      </div>
    );
  };

  const errorText = {
    unsupported: 'The live camera is not available here.',
    denied: 'Camera access was blocked.',
    unavailable: 'The camera could not be started.',
    timeout: 'The camera is taking too long to start.',
    paused: 'Take the photo in your phone camera.',
  }[cameraError];

  const altButtons = (big) => (
    <div className={`flex ${big ? 'flex-col w-full max-w-xs' : 'flex-row'} gap-3`}>
      <button
        type="button"
        onClick={openPhoneCamera}
        className={`inline-flex items-center justify-center gap-2 rounded-xl font-medium ${big ? 'px-5 py-4 bg-primary-600 hover:bg-primary-700 text-white text-base' : 'px-3 py-2 bg-white/10 hover:bg-white/20 text-white text-sm'}`}
      >
        <Camera className="w-5 h-5" /> {big ? 'Open phone camera' : 'Phone camera'}
      </button>
      <button
        type="button"
        onClick={openGallery}
        className={`inline-flex items-center justify-center gap-2 rounded-xl font-medium ${big ? 'px-5 py-4 bg-white/10 hover:bg-white/20 text-white text-base' : 'px-3 py-2 bg-white/10 hover:bg-white/20 text-white text-sm'}`}
      >
        <ImageIcon className="w-5 h-5" /> {big ? 'Choose from gallery' : 'Gallery'}
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[100] bg-black flex flex-col" style={{ height: '100dvh' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 pb-2 text-white" style={{ paddingTop: 'calc(0.75rem + env(safe-area-inset-top, 0px))' }}>
        <div>
          <div className="font-semibold text-base">{step === 'camera' ? 'Take a photo of your notes' : 'Adjust your photo'}</div>
          <div className="text-xs text-gray-300">
            {step === 'camera'
              ? (camState === 'failed' ? 'Use one of the options below' : 'Hold steady, then tap the big white button')
              : 'Drag the white corners to keep only the part you need (optional)'}
          </div>
        </div>
        <button type="button" onClick={handleClose} className="p-2 -mr-2 rounded-full hover:bg-white/10" title="Close" aria-label="Close camera">
          <X className="w-7 h-7" />
        </button>
      </div>

      {/* Hidden pickers: phone camera app + gallery */}
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handlePickedFile} className="hidden" />
      <input ref={galleryInputRef} type="file" accept="image/*" onChange={handlePickedFile} className="hidden" />

      {/* Body */}
      <div className="flex-1 min-h-0 flex items-center justify-center px-4 relative">
        {processing && step === 'camera' && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-black/70 text-white">
            <Loader2 className="w-8 h-8 animate-spin" />
            <span className="text-sm">Opening photo…</span>
          </div>
        )}

        {step === 'camera' && camState !== 'failed' && (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              onLoadedMetadata={onVideoReady}
              onPlaying={onVideoReady}
              className="max-w-full max-h-full rounded-lg bg-black"
            />
            {camState === 'starting' && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white">
                <Loader2 className="w-8 h-8 animate-spin" />
                <span className="text-sm">Starting camera… allow access if your phone asks</span>
              </div>
            )}
          </>
        )}

        {step === 'camera' && camState === 'failed' && (
          <div className="flex flex-col items-center text-center text-white gap-5 w-full">
            <Camera className="w-12 h-12 text-gray-400" />
            <div>
              <p className="font-medium">{errorText}</p>
              <p className="text-sm text-gray-300 mt-1">
                {cameraError === 'paused'
                  ? 'Didn’t open, or you came back without a photo? Try again below.'
                  : 'No problem — take the photo with your phone camera, or pick one you already took.'}
              </p>
            </div>
            {altButtons(true)}
            {cameraError === 'paused' && navigator.mediaDevices?.getUserMedia && (
              <button type="button" onClick={() => startStream(facingMode)} className="text-sm underline text-gray-300">
                Use the live camera here instead
              </button>
            )}
            {cameraError === 'denied' && (
              <p className="text-xs text-gray-400 max-w-xs">
                To use the live camera next time, allow Camera permission for this app in your phone&apos;s Settings → Apps.
              </p>
            )}
          </div>
        )}

        {step === 'crop' && imageSrc && (
          <div
            ref={cropAreaRef}
            className="relative inline-block select-none"
            style={{ touchAction: 'none' }}
          >
            <img
              ref={imgRef}
              src={imageSrc}
              alt="Your photo"
              draggable={false}
              className="block max-w-full object-contain"
              style={{ maxHeight: 'calc(100dvh - 230px)' }}
            />
            {/* Dim outside the crop box (clipped to the image) */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div
                className="absolute"
                style={{
                  left: `${crop.x * 100}%`,
                  top: `${crop.y * 100}%`,
                  width: `${crop.w * 100}%`,
                  height: `${crop.h * 100}%`,
                  boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)',
                }}
              />
            </div>
            <div
              onPointerDown={onPointerDown('move')}
              className="absolute border-2 border-white/80"
              style={{
                left: `${crop.x * 100}%`,
                top: `${crop.y * 100}%`,
                width: `${crop.w * 100}%`,
                height: `${crop.h * 100}%`,
                touchAction: 'none',
              }}
            >
              {handle('nw')}
              {handle('ne')}
              {handle('sw')}
              {handle('se')}
            </div>
          </div>
        )}
      </div>

      {loadError && (
        <p className="text-center text-sm text-red-300 px-4 pt-2">{loadError}</p>
      )}

      {/* Controls */}
      <div className="px-4 pt-4" style={{ paddingBottom: 'calc(1.25rem + env(safe-area-inset-bottom, 0px))' }}>
        {step === 'camera' && camState !== 'failed' && (
          <>
            <div className="flex items-center justify-center gap-8">
              <button type="button" onClick={openGallery} className="w-12 h-12 flex items-center justify-center rounded-full text-white bg-white/10 hover:bg-white/20" title="Choose from gallery" aria-label="Choose from gallery">
                <ImageIcon className="w-6 h-6" />
              </button>
              <button
                type="button"
                onClick={capture}
                disabled={camState !== 'ready'}
                className="w-20 h-20 rounded-full bg-white border-[6px] border-gray-400 active:scale-95 transition disabled:opacity-40"
                title="Take photo"
                aria-label="Take photo"
              />
              {hasMultipleCams ? (
                <button type="button" onClick={switchCamera} className="w-12 h-12 flex items-center justify-center rounded-full text-white bg-white/10 hover:bg-white/20" title="Switch camera" aria-label="Switch camera">
                  <SwitchCamera className="w-6 h-6" />
                </button>
              ) : <div className="w-12" />}
            </div>
            <p className="text-center text-xs text-gray-400 mt-3">
              Picture black or blurry?{' '}
              <button type="button" onClick={openPhoneCamera} className="underline text-gray-200">Use phone camera instead</button>
            </p>
          </>
        )}

        {step === 'crop' && (
          <div className="flex items-center justify-between gap-3 max-w-md mx-auto">
            <button type="button" onClick={retake} className="flex flex-col items-center text-white text-xs gap-1 px-2 hover:opacity-80">
              <RefreshCw className="w-6 h-6" /> Retake
            </button>
            <button type="button" onClick={rotate} className="flex flex-col items-center text-white text-xs gap-1 px-2 hover:opacity-80">
              <RotateCw className="w-6 h-6" /> Rotate
            </button>
            {!isFullCrop && (
              <button type="button" onClick={() => setCrop(FULL_CROP)} className="flex flex-col items-center text-white text-xs gap-1 px-2 hover:opacity-80">
                <Maximize className="w-6 h-6" /> Full photo
              </button>
            )}
            <button
              type="button"
              onClick={confirmCrop}
              disabled={processing}
              className="flex-1 max-w-[11rem] inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-white font-semibold text-base"
            >
              {processing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />} Add photo
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
